import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaPiggyBank, FaEgg, FaDog, FaLeaf, FaShieldAlt, FaHeart, FaSearch, FaFilter, FaFish, FaPaw } from 'react-icons/fa';
import { IconType } from 'react-icons/lib';
import { getProducts } from '../api/products';
import type { Product } from '../api/types';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const HeroSection = styled.section`
  position: relative;
  color: white;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  text-align: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/img/cow2.jpg') center/cover no-repeat;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(107, 142, 35, 0.5) 0%, rgba(244, 164, 96, 0.5) 100%);
    z-index: 1;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-family: 'Gamwulchi Free Gothic', 'Noto Sans KR', sans-serif;
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ContentSection = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.$active ? props.theme.colors.primary : 'white'};
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  border-radius: ${props => props.theme.borderRadius.full};
  font-weight: 500;
  transition: all ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.$active ? props.theme.colors.primaryDark : props.theme.colors.background};
  }
`;

const SearchBox = styled.div`
  position: relative;
  max-width: 300px;
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  padding-left: 50px;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 1rem;
  outline: none;
  transition: border-color ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textLight};
  font-size: 1.2rem;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing.xl};
`;

const ProductCard = styled(motion.div)`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.md};
  transition: all ${props => props.theme.transitions.normal};
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const ProductImageContainer = styled.div<{ $bgColor?: string }>`
  height: 250px;
  background: ${props => `linear-gradient(135deg, ${props.$bgColor}, ${props.$bgColor}CC)`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
  position: relative;
  overflow: hidden;
`;

const ProductMediaVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProductMediaImage = styled.div<{ $bgImage: string }>`
  width: 100%;
  height: 100%;
  background: ${props => `url(${props.$bgImage}) center/cover no-repeat`};
`;

const ProductContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const ProductHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProductCategory = styled.span`
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.9rem;
  font-weight: 500;
  word-break: keep-all;
`;

const ProductTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const ProductDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.md};
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const ProductFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const FeatureTag = styled.span`
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const ProductFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ViewDetailsButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-weight: 500;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #e74c3c;
  font-size: 1.1rem;
`;

// 제품 ID별 이미지 매핑 (고화질 WebM 사용)
const productImageMap: Record<number, string> = {
  1: '/img/main_pig5.webm',       // 뉴폴 (NewPol) - 고화질 WebM
  2: '/img/main_pig2.webm',       // 나우탄 (NAWTAN) - 고화질 WebM
  3: '/img/main_pig6.webm',   // 그로프로 (GroPro) - 고화질 WebM
  4: '/img/main_cow7.webm',       // 메탄킹 (CH4-King) - 고화질 WebM
  5: '/img/main_cow6.webm',       // 퀼라야 (Quillaja) - 고화질 WebM
  6: '/img/main_cow9.webm',       // 나우산 777 (NAWSAN-777) - 고화질 WebM
  7: '/img/main_chicken2l.webm',  // 몰임프린트 (Molimprint) - 고화질 WebM
  8: '/img/main_sheep1.webm',     // 몰리맥스 AE (Molimax AE) - 고화질 WebM
  9: '/img/main_dog1.webm',       // 나우스위트3 (NAW-SWEET 3) - 고화질 WebM
};

const ProductsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(t('products.categories.all'));
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Type cast icons
  const PigIcon = FaPiggyBank as IconType;
  const ChickenIcon = FaEgg as IconType;
  const CowIcon = FaDog as IconType;
  const LeafIcon = FaLeaf as IconType;
  const ShieldIcon = FaShieldAlt as IconType;
  const HeartIcon = FaHeart as IconType;
  const SearchIconComponent = FaSearch as IconType;
  const FilterIcon = FaFilter as IconType;
  const FishIcon = FaFish as IconType;
  const PawIcon = FaPaw as IconType;

  const categories = [
    { name: t('products.categories.all'), icon: <FilterIcon /> },
    { name: t('products.categories.pig'), icon: <PigIcon /> },
    { name: t('products.categories.poultry'), icon: <ChickenIcon /> },
    { name: t('products.categories.beef'), icon: <CowIcon /> },
    { name: t('products.categories.fish'), icon: <FishIcon /> },
    { name: t('products.categories.pet'), icon: <PawIcon /> }
  ];

  // 언어 변경 시 카테고리를 '전체'로 초기화
  useEffect(() => {
    setActiveCategory(t('products.categories.all'));
  }, [i18n.language, t]);

  // API에서 제품 데이터 가져오기
  useEffect(() => {
    const fetchProductsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getProducts({ lang: i18n.language });
        setProducts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsData();
  }, [i18n.language]);

  // 카테고리 번역 함수
  const translateCategory = (category: string): string => {
    const categoryKey = `products.categoryMap.${category}` as const;
    return t(categoryKey, category); // 번역이 없으면 원본 반환
  };

  const filteredProducts = products.filter(product => {
    const categories = product.category.split(',').map(cat => cat.trim());
    const matchesCategory = activeCategory === t('products.categories.all') || categories.includes(activeCategory);
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Container>
      <HeroSection>
        <HeroTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {t('products.title')}
        </HeroTitle>
        <HeroSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t('products.subtitle')}
        </HeroSubtitle>
      </HeroSection>

      <ContentSection>
        <FilterSection>
          <CategoryTabs>
            {categories.map(category => (
              <CategoryTab
                key={category.name}
                $active={activeCategory === category.name}
                onClick={() => setActiveCategory(category.name)}
              >
                {category.icon}
                {category.name}
              </CategoryTab>
            ))}
          </CategoryTabs>

          <SearchBox>
            <SearchIcon>
              <SearchIconComponent />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder={t('products.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
        </FilterSection>

        {loading && (
          <LoadingMessage>{t('products.loading') || 'Loading products...'}</LoadingMessage>
        )}

        {error && (
          <ErrorMessage>
            {t('products.error') || 'Error loading products'}: {error}
          </ErrorMessage>
        )}

        {!loading && !error && (
          <>
            <ProductsGrid>
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  <ProductImageContainer $bgColor={product.bgColor}>
                    {(() => {
                      const imageUrl = productImageMap[product.id] || '/img/product_Icon.png';
                      const isVideo = imageUrl.endsWith('.webm') || imageUrl.endsWith('.mp4');

                      if (isVideo) {
                        return (
                          <ProductMediaVideo
                            src={imageUrl}
                            autoPlay
                            loop
                            muted
                            playsInline
                          />
                        );
                      }
                      return <ProductMediaImage $bgImage={imageUrl} />;
                    })()}
                  </ProductImageContainer>

                  <ProductContent>
                    <ProductHeader>
                      {product.category.split(',').map((cat, idx) => (
                        <ProductCategory key={idx}>{translateCategory(cat.trim())}</ProductCategory>
                      ))}
                    </ProductHeader>

                    <ProductTitle>{product.name}</ProductTitle>
                    <ProductDescription>{product.excerpt || product.description}</ProductDescription>

                    <ProductFeatures>
                      {(product.features || []).map((feature, idx) => (
                        <FeatureTag key={idx}>
                          {idx === 0 && <LeafIcon />}
                          {idx === 1 && <ShieldIcon />}
                          {idx === 2 && <HeartIcon />}
                          {idx === 3 && <LeafIcon />}
                          {feature}
                        </FeatureTag>
                      ))}
                    </ProductFeatures>

                    <ProductFooter>
                      <ViewDetailsButton onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/products/${product.id}`);
                      }}>
                        {t('products.viewDetails')}
                      </ViewDetailsButton>
                    </ProductFooter>
                  </ProductContent>
                </ProductCard>
              ))}
            </ProductsGrid>

            {filteredProducts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                {t('products.noResults')}
              </div>
            )}
          </>
        )}
      </ContentSection>
    </Container>
  );
};

export default ProductsPage;